import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { AuthService, EntityService, User } from '@app/core';
import {
  clearPlanExpiryPopupPending,
  expiringCompanyNotices,
  hasPlanExpiryPopupPending,
  PlanExpiryNotice,
  planExpiryDaysLabel
} from '@core/helpers/plan-expiry';
import { PmdModal } from '@app/shared/propeller';
import { Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

const OWNER_SELECTION_WAIT_MS = 2000;

@Component({
  selector: 'app-plan-expiry-popup',
  templateUrl: './plan-expiry-popup.component.html',
  styleUrls: ['./plan-expiry-popup.component.scss']
})
export class PlanExpiryPopupComponent implements AfterViewInit, OnDestroy {
  @ViewChild(PmdModal) planExpiryModal: PmdModal;
  notices: PlanExpiryNotice[] = [];
  daysLabel = planExpiryDaysLabel;

  private readonly destroy$ = new Subject<void>();
  private selectionSub: Subscription;
  private waitTimer: ReturnType<typeof setTimeout>;
  private settled = false;

  constructor(
    private readonly authService: AuthService,
    private readonly entityService: EntityService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this.tryShow();
  }

  close(): void {
    this.planExpiryModal?.hide();
  }

  ngOnDestroy(): void {
    this.clearWait();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryShow(): void {
    if (!hasPlanExpiryPopupPending()) {
      return;
    }

    const user = this.authService.user;
    if (!user) {
      return;
    }

    if (!this.isEligible(user)) {
      this.finish([]);
      return;
    }

    if (this.isProfileSetupPending(user)) {
      return;
    }

    const outlets = this.accountOutlets();
    if (user.isOwner && !outlets.length) {
      this.waitForEntities();
      return;
    }

    this.finish(outlets);
  }

  private isEligible(user: User): boolean {
    if (user.isDemo || user.isAdmin || user.isLeadAdmin || user.isSuperAdmin) {
      return false;
    }

    return !!(user.isOwner || user.isOutlet || user.isUploader || user.isReviewer);
  }

  private isProfileSetupPending(user: User): boolean {
    if (!user.isOnboarded) {
      return true;
    }

    try {
      const stepData = localStorage.getItem('setupProfileData');
      const completedStep = localStorage.getItem('profileSetupCurrentPage');
      return !!(stepData && completedStep !== 'completed');
    } catch {
      return false;
    }
  }

  private accountOutlets(): any[] {
    const outlets = this.entityList(this.entityService.getEntities()).filter(entity => this.isOutletId(entity?.id));
    if (outlets.length) {
      return outlets;
    }

    const company: any = this.authService.user?.company;
    if (!company) {
      return [];
    }

    const companyOutlets = (company.outlets || []).filter(outlet => this.isOutletId(outlet?.id));
    if (companyOutlets.length) {
      return companyOutlets.map(outlet => ({
        id: outlet.id,
        name: outlet.name || company.name,
        group: company.name,
        parentId: company.id,
        subscriptionDate: outlet.subscriptionDate ?? company.subscriptionDate
      }));
    }

    if (!company.id && !company.name) {
      return [];
    }

    return [{
      id: company.id || company.name,
      name: company.name,
      group: company.name,
      parentId: company.id,
      subscriptionDate: company.subscriptionDate
    }];
  }

  private waitForEntities(): void {
    this.selectionSub = this.entityService.entitiesSubject$.pipe(
      filter(entities => this.entityList(entities).some(entity => this.isOutletId(entity?.id))),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.clearWait();
      this.finish(this.accountOutlets());
    });

    this.waitTimer = setTimeout(() => {
      this.selectionSub?.unsubscribe();
      this.selectionSub = null;
      this.finish(this.accountOutlets());
    }, OWNER_SELECTION_WAIT_MS);
  }

  private finish(outlets: any[]): void {
    if (this.settled) {
      return;
    }

    this.settled = true;
    this.clearWait();
    clearPlanExpiryPopupPending();

    this.notices = expiringCompanyNotices(outlets);
    if (!this.notices.length) {
      return;
    }

    this.changeDetectorRef.detectChanges();
    this.planExpiryModal?.show();
  }

  private entityList(value: unknown): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (value && typeof value === 'object') {
      return [value];
    }

    return [];
  }

  private isOutletId(id: unknown): boolean {
    return id != null && id !== '' && id !== '0' && id !== 0;
  }

  private clearWait(): void {
    if (this.waitTimer) {
      clearTimeout(this.waitTimer);
      this.waitTimer = null;
    }

    this.selectionSub?.unsubscribe();
    this.selectionSub = null;
  }
}
